import type { JSX } from 'solid-js'
import AccountSidebar from './AccountSidebar'
import Footer from '/web/shared/aifans/Footer'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import SettingsInput from '/web/shared/aifans/SettingsInput'

import { Save, X } from 'lucide-solid'
import { Component, createEffect, createSignal, onMount } from 'solid-js'

import Button from '../../shared/Button'

import Modal from '../../shared/Modal'
import TextInput from '../../shared/TextInput'
import { getStrictForm, setComponentPageTitle } from '../../shared/util'
import { adminStore, settingStore, toastStore, userStore } from '../../store'

import { rootModalStore } from '/web/store/root-modal'
import { useNavigate } from '@solidjs/router'

export default function UserSettings() {
  const [pass, setPass] = createSignal(false)
  let formRef!: HTMLFormElement

  const nav = useNavigate()
  const state = userStore()
  const admin = adminStore()
  const settings = settingStore()

  onMount(() => {
    setComponentPageTitle('Settings')
  })

  const submit: JSX.EventHandler<HTMLFormElement, SubmitEvent> = (e: SubmitEvent) => {
    e.preventDefault()
    const body = getStrictForm(formRef, { handle: 'string', email: 'string', phone: 'string' })
    let payload: { handle: string; avatar?: File; email: string; phone?: string } = {
      handle: body.handle,
      avatar: undefined,
      email: body.email,
    }

    if (body.phone) {
      payload.phone = body.phone
    }

    userStore.updateProfile(payload)
  }

  createEffect(() => {
    userStore.getProfile()
    userStore.getConfig()
  })

  return (
    <>
      <HeaderContainer />
      <main class="settings page-bg bg-[#141414]f relative overflow-hidden bg-main pb-20 pt-20 sm:pt-28 md:bg-none md:pb-36 lg:max-h-[1000px]">
        <div class="container relative z-20">
          <div class="wrapper gap-8 lg:flex">
            <AccountSidebar />

            {/* right */}
            <div class="right flex w-full flex-col">
              {/* head */}
              <div class="head gray-to-tran mb-7 rounded-xl text-[28px] font-semibold sm:p-4">
                <h2>Settings</h2>
              </div>
              <div class="w-full md:w-1/2">
                <form ref={formRef!} onSubmit={submit} aria-label="Edit profile">
                  <div class="mb-6 flex flex-col gap-6">
                    <div>
                      <TextInput
                        fieldName="userid"
                        value={state.user?._id}
                        disabled
                        class="hidden"
                      />

                      <TextInput
                        fieldName="username"
                        value={state.user?.username}
                        disabled
                        class="hidden"
                      />

                      <label for="first_name" class="mb-2 block text-sm font-medium">
                        Username
                      </label>
                      <input
                        type="text"
                        id="handle"
                        name="handle"
                        class="w-full rounded-xl border border-transparent bg-white/10 px-4 py-2 outline-none transition-all duration-300 "
                        placeholder="John"
                        required
                        value={state.profile?.handle}
                      />
                    </div>

                    <div>
                      <label for="email" class="mb-2 block text-sm font-medium">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        class="w-full rounded-xl border border-transparent bg-white/10 px-4 py-2 outline-none transition-all duration-300"
                        placeholder="john.doe@company.com"
                        value={state.profile?.email}
                        required
                      />
                    </div>

                    <div>
                      <label for="phone" class="mb-2 block text-sm font-medium">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        class="w-full rounded-xl border border-transparent bg-white/10 px-4 py-2 outline-none transition-all duration-300"
                        placeholder="123-456-6789"
                        value={state.profile?.phone || ''}
                      />
                    </div>
                  </div>
                  <div>
                    <button
                      class="justify-self-end rounded-xl border border-transparent bg-white/10 px-4 py-2 transition-all duration-300 hover:border-cosplay-red hover:bg-cosplay-red/10"
                      onClick={(e) => {
                        e.preventDefault()
                        setPass(true)
                      }}
                    >
                      Change Password
                    </button>
                  </div>
                  <div class="flex justify-end">
                    <button
                      type="submit"
                      class="mt-6 rounded-xl border border-transparent bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200 px-4 py-2 transition-all duration-300 hover:bg-cosplay-red/10"
                    >
                      Save Settings
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* bg */}
        <div class="radial-bg bottom-[-40%] left-[-250px] right-0 h-[1000px] w-[900px] opacity-10 md:hidden"></div>
      </main>
      <Footer />
      <PasswordModal show={pass()} close={() => setPass(false)} />
    </>
  )
}

const FormSettingsBoxes = () => {
  return (
    <div class="items gray-to-tran h-full w-full rounded-xl p-6">
      <div class="wrap grid max-w-[45rem] items-start justify-start gap-x-8 gap-y-12 text-lg md:grid-cols-2">
        {/* username */}
        <SettingsInput title="Username" placeholder="Bolt81" type="text" value="Bolt81" />

        {/* email */}
        <SettingsInput
          title="Email"
          placeholder="jbolter@mail.com"
          type="email"
          value="jbolter@mail.com"
        />

        {/* phone */}
        <SettingsInput title="Phone" placeholder="Phone" type="phone" value="" />

        <div class="head border-b pb-3 sm:hidden">Security</div>
        {/* password */}
        <SettingsInput title="Password" placeholder="Password" type="password" value="" />
      </div>
    </div>
  )
}

const PasswordModal: Component<{ show: boolean; close: () => void }> = (props) => {
  let ref: any
  const save = () => {
    const body = getStrictForm(ref, { newPassword: 'string', confirmPassword: 'string' })
    if (body.newPassword !== body.confirmPassword) {
      toastStore.warn(`Your passwords do not match`)
      return
    }

    userStore.changePassword(body.newPassword, props.close)
  }

  rootModalStore.addModal({
    id: 'user-password-change',
    element: (
      <Modal
        show={props.show}
        close={props.close}
        title="Change Password"
        ariaLabel="Change Password"
        ariaDescription="This window lets you change your account password"
        footer={
          <>
            {' '}
            <Button schema="secondary" onClick={props.close}>
              <X /> Cancel
            </Button>
            <Button onClick={save}>
              <Save /> Update
            </Button>
          </>
        }
      >
        <div>
          <form ref={ref} class="flex flex-col gap-2">
            <div class="w-full justify-center">Update your password</div>
            <TextInput
              type="password"
              fieldName="newPassword"
              required
              placeholder="New Password"
            />
            <TextInput
              type="password"
              fieldName="confirmPassword"
              required
              placeholder="Repeat Password"
            />
          </form>
        </div>
      </Modal>
    ),
  })

  return null
}
